/**
 * Feature E02-F03 — "Создание и обновление проекта" request/response contracts.
 *
 * 1:1 port of the legacy n8n Project / Create / Links / DelFile / Att branches
 * (docs/source-n8n/reference/{Project,Create,Links,DelFile,Att,_common}.md).
 * Single source of truth for the shapes the api validates and the web client
 * sends/reads:
 *
 *   - POST   /projects                 create_manual   (T01)
 *   - POST   /projects/from-email      create_from_email (T02)
 *   - PATCH  /projects/:id             update_project  (T03, T04, T05)
 *   - POST   /projects/:id/links       add_link        (T05)
 *   - DELETE /projects/:id/links/:i    delete_link     (T05)
 *   - DELETE /projects/:id/files/:i    delete_file     (T05)
 *   - GET    /clients                  clients-cache (read side, T06/T09)
 *   - POST   /clients/cache            clients-cache write (update_clients parity, T06)
 *
 * IDOR invariant (carried over unchanged): every owner/role/userId is taken from
 * the validated session on the api side, NEVER from these bodies. Any such field
 * present in a request body is ignored — it is intentionally absent from these
 * schemas so it can never be trusted.
 *
 * Lives in its OWN file (not re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
import { manualContextSchema, projectLinkSchema, fileExtractStatusSchema } from '../jsonb.js';
import { isValidEmail } from './email-send.js';
// ---------------------------------------------------------------------------
// T01 — POST /projects (create_manual)
// ---------------------------------------------------------------------------
/**
 * create_manual takes NO body fields the client controls — owner_id is the
 * session identity, role_name/stage/status/data are server-fixed. An optional
 * `role_name` placeholder is allowed for the future dashboard quick-create, but
 * the legacy node hard-codes 'New Position' and ignores any body field.
 */
export const createManualProjectInputSchema = z
    .object({
    role_name: z.string().trim().min(1).max(255).optional(),
})
    .strip();
/** Both create flows return the new project id (`{ success:true, project_id }`). */
export const createProjectResultSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    project_id: z.number().int(),
});
/**
 * E12-F17-T15 — DELETE /projects/:id/draft: drop an abandoned, still-empty manual
 * draft. `deleted:false` = the project is no longer a pristine draft (or not the
 * caller's) and was left untouched — never an error for the SPA.
 */
export const discardDraftResultSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    deleted: z.boolean(),
});
// ---------------------------------------------------------------------------
// T02 — POST /projects/from-email (create_from_email)
// ---------------------------------------------------------------------------
/** The only client-controlled input is the source email id. */
export const createFromEmailInputSchema = z
    .object({
    // Legacy front-end posted `email_id`; accept `emailId` too for the REST client.
    email_id: z.coerce.number().int().positive().optional(),
    emailId: z.coerce.number().int().positive().optional(),
    /**
     * Att-group gate (docs/source-n8n/reference/Att.md, "gated by
     * transfer_attachments=true"): when true, the source email's attachments are
     * fetched ON-DEMAND at create time and uploaded into the project's Drive
     * folder, their webViewLinks appended to `data.manual_context.files[]`.
     * Default false = current behaviour (no transfer) → back-compatible.
     */
    transfer_attachments: z.boolean().optional().default(false),
})
    .refine((v) => v.email_id !== undefined || v.emailId !== undefined, {
    message: 'email_id is required',
    path: ['email_id'],
});
/**
 * The create-from-email options usable WITHOUT an email id in the body — for the
 * inbox action route `POST /emails/:id/create-project`, which carries the email
 * id in the PATH (not the body). Parse `request.body ?? {}` with this to read the
 * attachment-transfer gate; an empty body yields `{ transfer_attachments:false }`
 * (back-compat: existing callers that send no body → no transfer). `.passthrough()`
 * tolerates any other keys the client may add later.
 */
export const createFromEmailOptionsSchema = z
    .object({
    transfer_attachments: z.boolean().optional().default(false),
})
    .passthrough();
/**
 * The structured fields the create_from_email LLM extracts from the email body
 * (ai_prompts key `create_from_email`). Tolerant: every field optional/nullable
 * because the prompt sets unknown fields to null and the parser falls back to the
 * email envelope. `.passthrough()` keeps any extra keys the model returns.
 */
export const emailExtractionSchema = z
    .object({
    client_name: z.string().nullish(),
    role_name: z.string().nullish(),
    contact_name: z.string().nullish(),
    /**
     * Optional split of the contact name — parity with the manual-edit path's
     * {@link contactEditSchema} `{first_name,last_name}` (project-detail-card.ts).
     *
     * IMPORTANT: there is NO first/last column on `contacts` (single
     * `Contact.name VarChar(255)`), so NO migration is needed. These are joined
     * server-side into the single `Contact.name` before persisting, exactly as
     * the manual edit path already does. Canonical join (apply in BOTH
     * `applyEmailFallbacks` (projectEmailExtraction.ts) and `createProjectFromEmail`'s
     * contact-create block (projectMutations.ts service)):
     *
     *   const joined = [contact_first_name, contact_last_name]
     *     .filter((p) => p && p.trim())
     *     .map((p) => p!.trim())
     *     .join(' ');
     *   contactName = joined || contact_name || email.sender_name || null;
     *
     * `contact_name` is kept as the derived/fallback field so already-created
     * projects and any extraction path that does NOT split stay valid (back-compat).
     */
    contact_first_name: z.string().nullish(),
    contact_last_name: z.string().nullish(),
    contact_email: z.string().nullish(),
    contact_cc: z
        .array(z.object({ name: z.string().nullish(), email: z.string().nullish() }).passthrough())
        .nullish(),
    location: z.string().nullish(),
    salary_range: z.string().nullish(),
    urgency: z.string().nullish(),
    summary: z.string().nullish(),
    links: z
        .array(z.object({ url: z.string(), title: z.string().nullish() }).passthrough())
        .nullish(),
})
    .passthrough();
// ---------------------------------------------------------------------------
// T03/T04 — PATCH /projects/:id (universal update_project)
// ---------------------------------------------------------------------------
/**
 * E02-F03-T10 — the ONE rule for a user-entered contact email, shared by the api
 * (`assertContactEmail` → 400 `VALIDATION_ERROR`) and the web header (inline
 * highlight + Save blocked BEFORE the PATCH is sent), so the two can never
 * disagree. `null`/`undefined`/`''` are acceptable (they leave or clear the
 * field); any other value must pass {@link isValidEmail}. The exact value is
 * checked untrimmed — it is what the api would persist.
 *
 * Returns `null` when acceptable, else a human-readable reason (shown verbatim in
 * the web toast/inline error — Ксения 12.10: `ksenia.bussen#gmail.com` gave three
 * reasonless «Could not save the project» toasts).
 */
export function contactEmailError(email) {
    if (typeof email !== 'string' || email.length === 0)
        return null;
    if (isValidEmail(email))
        return null;
    return `Invalid contact email address: "${email}". Use the name@company.com format.`;
}
/** Contact sub-object accepted by update_project (`updates.contact`). */
export const updateContactSchema = z
    .object({
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    name: z.string().nullish(),
    email: z.string().nullish(),
})
    .passthrough();
/**
 * KS3-05 — набор получателей одного документа: кому и в копию.
 *
 * Адреса хранятся строками, а не ссылками на контакты: список сознательно
 * допускает адрес, которого нет ни в справочнике, ни в команде («возможность
 * гибко дополнять»), и такой адрес не должен требовать заведения контакта.
 */
export const emailRecipientsEntrySchema = z.object({
    to: z.array(z.string()).default([]),
    cc: z.array(z.string()).default([]),
    /** E05-F06-T11 — remembered blind copies (absent on entries saved before). */
    bcc: z.array(z.string()).optional(),
});
/**
 * `updates` — the universal mutation bag (legacy `body.updates`, a JSON object or
 * a JSON string in multipart). Every key optional; unknown keys merge into
 * `projects.data` (parity: arbitrary `updates.*` are merged into data). IDOR
 * fields (owner_id/role/userId) are deliberately absent and ignored on the api.
 */
export const projectUpdatesSchema = z
    .object({
    role_name: z.string().trim().max(255).optional(),
    stage: z.coerce.number().int().min(1).max(6).optional(),
    status: z.string().max(50).optional(),
    position_type_id: z.coerce.number().int().nullable().optional(),
    client_id: z.coerce.number().int().optional(),
    client_name: z.string().max(255).optional(),
    /**
     * PK-QA-16: human-entered company website persisted to `Client.website`.
     * The `client_` prefix is deliberate — like `client_name`/`client_id` it
     * targets the resolved `Client` row and is EXPLICITLY consumed by the
     * PATCH route, so it never falls through `.passthrough()` into
     * `projects.data`. Normalized server-side: trim, empty string → null.
     */
    client_website: z.string().trim().max(255).nullable().optional(),
    /** Force-create a new client even when a link exists (parity: forceNew). */
    forceNewClient: z.boolean().optional(),
    contact: updateContactSchema.optional(),
    manual_context: manualContextSchema.optional(),
    cc_emails: z.array(z.string()).optional(),
    /**
     * KS3-05 — сохранённые получатели по каждому отправляемому документу.
     *
     * Ксения: «нужно видеть, кому уходят письма + возможность добавлять там
     * получателей … и сохранять это, чтобы не сбрасывалось, если повторно отправить
     * надо». До этого набор получателей жил только в открытом диалоге: закрыл —
     * и следующая отправка снова начиналась с одного адреса из карточки проекта.
     *
     * Ключ — `item_key` отправки (`client_followup_email`, `kickoff_sheet`, …), то
     * есть у каждого документа свой список: сорсинг-команда и клиент — разные люди.
     */
    email_recipients: z.record(emailRecipientsEntrySchema).optional(),
    kickoff_notes: z.string().optional(),
    kickoff_transcript: z.string().optional(),
    kickoff_questions: z.unknown().optional(),
    meeting_completed: z.boolean().optional(),
    generation_notes: z.string().optional(),
})
    .passthrough();
/**
 * JSON body for PATCH /projects/:id (the non-multipart path). The multipart path
 * carries `updates` as a stringified JSON field plus `files[]` — the route parses
 * the string with {@link projectUpdatesSchema}. `updates` may itself arrive as a
 * raw JSON string (legacy front-end behaviour), so accept string | object.
 */
export const updateProjectBodySchema = z.object({
    updates: z.union([projectUpdatesSchema, z.string()]).optional(),
});
/**
 * A file reference stored in `data.manual_context.files`. Beyond the base
 * `{name,url}` it now carries the PK-QA-4 upload-time text-extraction result —
 * mirroring the `projectLinkSchema` `{content,fetch_status,fetched_at}` triple so
 * files and links share ONE "attachment with extracted content + status" model:
 *   - `content`       — extracted plain text (capped at 50k chars), fed to the
 *                       pre-kickoff LLM prompt. Absent when nothing was extracted.
 *   - `extract_status`— ok | empty | failed | unsupported (see
 *                       {@link FILE_EXTRACT_STATUSES}). Drives a UI badge.
 *   - `extracted_at`  — ISO timestamp of the extraction attempt.
 * All three are optional so legacy `{name,url}` rows created before PK-QA-4 stay
 * valid, and `.passthrough()` keeps any future keys.
 */
export const projectFileSchema = z
    .object({
    name: z.string(),
    url: z.string(),
    content: z.string().optional(),
    extract_status: fileExtractStatusSchema.optional(),
    extracted_at: z.string().optional(),
})
    .passthrough();
/**
 * The full project returned after a successful update (parity with get_1_project:
 * project + artifacts + tasks + events). Kept permissive — the read-side feature
 * owns the precise Project shape; here we only pin the envelope.
 */
export const projectDetailResultSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    project: z.record(z.string(), z.unknown()),
    artifacts: z.array(z.record(z.string(), z.unknown())).default([]),
    tasks: z.array(z.record(z.string(), z.unknown())).default([]),
    events: z.array(z.record(z.string(), z.unknown())).default([]),
});
// ---------------------------------------------------------------------------
// T05 — links & files
// ---------------------------------------------------------------------------
/** POST /projects/:id/links — add a manual link (url scraped server-side). */
export const addLinkInputSchema = z.object({
    url: z
        .string()
        .trim()
        .regex(/^https?:\/\//i, 'URL must start with http:// or https://'),
});
/**
 * DELETE /projects/:id/links/:index — optional identity guard (E02-F03-T08).
 *
 * The path index alone is a POSITIONAL address into a shared jsonb array, so it
 * goes stale the moment anyone else adds/removes a link on the same project:
 * the request then deletes a DIFFERENT row and still answers 200. `url` turns
 * the index into a mere hint — the server deletes the link carrying this URL
 * (and no-ops idempotently when it is already gone). Optional for backward
 * compatibility: callers that omit it keep the legacy positional behaviour.
 */
export const deleteLinkQuerySchema = z.object({
    url: z.string().trim().min(1).optional(),
});
/** Generic `{ success:true }` ack used by add/delete link/file. */
export const successAckSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    message: z.string().optional(),
});
// re-export the link shape for web consumers (icon-by-domain rendering).
export { projectLinkSchema };
// ---------------------------------------------------------------------------
// T06/T09 — clients cache (autocomplete source; update_clients parity)
// ---------------------------------------------------------------------------
/** A single client cache entry (id,name) for autocomplete (state.clients). */
export const clientCacheEntrySchema = z.object({
    id: z.number().int(),
    name: z.string(),
});
/** GET /clients → the clients cache array (id,name ORDER BY name ASC). */
export const clientsCacheSchema = z.array(clientCacheEntrySchema);
/**
 * POST /clients/cache write body (update_clients parity): a JSON array of
 * {id,name}. The route authorizes this with a shared-secret X-Auth-Token header;
 * an invalid array → 400, a mismatched token → 403.
 */
export const clientsCacheWriteSchema = clientsCacheSchema;
//# sourceMappingURL=projectMutations.js.map